<template>
  <div class="overflow-x-auto">
    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <thead class="bg-gray-50 dark:bg-gray-900/50">
        <tr>
          <th
            v-for="column in columns"
            :key="column.key"
            scope="col"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {{ column.label }}
          </th>
          <th scope="col" class="relative px-6 py-3">
            <span class="sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
        <tr
          v-for="location in locations"
          :key="location.id"
          class="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer"
          @click="$emit('view', location)">
          <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500">
            <div class="flex items-center gap-2">
              <span>{{ location.name }}</span>
              <span
                v-if="location.is_primary"
                class="inline-flex rounded-md bg-primary-100 dark:bg-primary-900/40 px-2 py-0.5 text-xs font-medium text-primary-700 dark:text-primary-300">
                Main
              </span>
            </div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
            {{ formatLocationType(location.location_type) }}
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <span
              class="inline-flex rounded-md px-2 py-0.5 text-xs font-medium"
              :class="statusClass(location.status)">
              {{ formatStatus(location.status) }}
            </span>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
            {{ location.business_name }}
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
            {{ location.address || shortAddress(location) || 'Not set' }}
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <span
              class="inline-flex rounded-md px-2 py-0.5 text-xs font-medium"
              :class="analysisReadinessClass(location)"
              :title="analysisReadinessTooltip(location)">
              {{ analysisReadinessLabel(location) }}
            </span>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-right text-sm">
            <button
              type="button"
              class="text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300"
              @click="$emit('view', location)">
              View
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
defineProps({
  locations: {
    type: Array,
    required: true,
    validator: (value) => Array.isArray(value)
  }
})

defineEmits(['view'])

const columns = [
  { key: 'name', label: 'Location' },
  { key: 'type', label: 'Type' },
  { key: 'status', label: 'Status' },
  { key: 'business', label: 'Business' },
  { key: 'address', label: 'Address' },
  { key: 'analysis', label: 'Analysis Data' }
]

function formatLocationType(type) {
  return {
    physical_office: 'Physical Office',
    service_area_only: 'Service Area Only',
    hybrid_location: 'Hybrid Location'
  }[type] || 'Physical Office'
}

function formatStatus(status) {
  return {
    active: 'Active',
    closed: 'Closed',
    temporarily_closed: 'Temporarily Closed',
    needs_client_confirmation: 'Needs Confirmation'
  }[status] || 'Active'
}

function statusClass(status) {
  return {
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    closed: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    temporarily_closed: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    needs_client_confirmation: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
  }[status] || 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
}

function shortAddress(location) {
  return [location.city, location.state].filter(Boolean).join(', ')
}

function missingDetails(location) {
  const fields = ['phone_number', 'website_url', 'place_id']
  if (location.location_type === 'service_area_only') {
    fields.unshift('city', 'state')
  } else {
    fields.unshift('address_line_1', 'city', 'state', 'zip', 'country')
  }
  return fields.filter((field) => !location[field])
}

function analysisReadinessLabel(location) {
  if (location.status !== 'active') return 'Excluded'
  return missingDetails(location).length ? 'Limited' : 'Complete'
}

function analysisReadinessClass(location) {
  if (location.status !== 'active') {
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  }

  return missingDetails(location).length ?
    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
}

function analysisReadinessTooltip(location) {
  if (location.status !== 'active') return 'Closed, temporarily closed, and unconfirmed locations are excluded from analysis readiness.'
  const missing = missingDetails(location)
  if (!missing.length) return 'Required location details are present for analysis.'

  return `Missing ${missing.map((field) => field.replace(/_/g, ' ')).join(', ')}.`
}
</script>
